import { db } from '../database/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

const COURSES_COLLECTION = 'courses';
const ONLINECLASS_COLLECTION = 'onlineclass';
const CLASSES_COLLECTION = 'classes';
const ATTENDANCE_COLLECTION = 'attendance';

// ============================================
// COURSE MANAGEMENT
// ============================================

// Get all courses
export const getAllCourses = async () => {
  try {
    const coursesRef = collection(db, COURSES_COLLECTION);
    const q = query(coursesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

// Get course by ID
export const getCourseById = async (courseId) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const courseDoc = await getDoc(courseRef);
    
    if (courseDoc.exists()) {
      return { id: courseDoc.id, ...courseDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching course:', error);
    throw error;
  }
};

// Add new course
export const addCourse = async (courseData) => {
  try {
    const coursesRef = collection(db, COURSES_COLLECTION);
    const newCourse = {
      ...courseData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(coursesRef, newCourse);
    return { id: docRef.id, ...newCourse };
  } catch (error) {
    console.error('Error adding course:', error);
    throw error;
  }
};

// Update course
export const updateCourse = async (courseId, courseData) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    const updateData = {
      ...courseData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(courseRef, updateData);
    return { id: courseId, ...updateData };
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

// Delete course
export const deleteCourse = async (courseId) => {
  try {
    const courseRef = doc(db, COURSES_COLLECTION, courseId);
    await deleteDoc(courseRef);
    return true;
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

// ============================================
// ENROLLMENT MANAGEMENT
// ============================================

// Get all enrollments
export const getAllEnrollments = async () => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const q = query(enrollmentsRef, orderBy('startDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching enrollments:', error);
    throw error;
  }
};

// Get enrollments by course ID
export const getEnrollmentsByCourseId = async (courseId) => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const q = query(
      enrollmentsRef,
      where('courseId', '==', courseId),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching course enrollments:', error);
    throw error;
  }
};

// Get enrollments by student mobile
export const getEnrollmentsByMobile = async (mobile) => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const q = query(
      enrollmentsRef,
      where('mobile', '==', mobile),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching student enrollments:', error);
    throw error;
  }
};

// Add new enrollment
export const addEnrollment = async (enrollmentData) => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const newEnrollment = {
      ...enrollmentData,
      status: 'active',
      completedClasses: 0,
      attendedDays: 0,
      absentDays: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(enrollmentsRef, newEnrollment);
    return { id: docRef.id, ...newEnrollment };
  } catch (error) {
    console.error('Error adding enrollment:', error);
    throw error;
  }
};

// Update enrollment
export const updateEnrollment = async (enrollmentId, enrollmentData) => {
  try {
    const enrollmentRef = doc(db, ONLINECLASS_COLLECTION, enrollmentId);
    const updateData = {
      ...enrollmentData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(enrollmentRef, updateData);
    return { id: enrollmentId, ...updateData };
  } catch (error) {
    console.error('Error updating enrollment:', error);
    throw error;
  }
};

// Delete enrollment
export const deleteEnrollment = async (enrollmentId) => {
  try {
    const enrollmentRef = doc(db, ONLINECLASS_COLLECTION, enrollmentId);
    await deleteDoc(enrollmentRef);
    return true;
  } catch (error) {
    console.error('Error deleting enrollment:', error);
    throw error;
  }
};

// ============================================
// CLASS SCHEDULE MANAGEMENT
// ============================================

// Get all classes
export const getAllClasses = async () => {
  try {
    const classesRef = collection(db, CLASSES_COLLECTION);
    const q = query(classesRef, orderBy('classDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching classes:', error);
    throw error;
  }
};

// Get classes by course ID
export const getClassesByCourseId = async (courseId) => {
  try {
    const classesRef = collection(db, CLASSES_COLLECTION);
    const q = query(
      classesRef,
      where('courseId', '==', courseId),
      orderBy('classDate', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching course classes:', error);
    throw error;
  }
};

// Add new class
export const addClass = async (classData) => {
  try {
    const classesRef = collection(db, CLASSES_COLLECTION);
    const newClass = {
      ...classData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(classesRef, newClass);
    return { id: docRef.id, ...newClass };
  } catch (error) {
    console.error('Error adding class:', error);
    throw error;
  }
};

// Update class
export const updateClass = async (classId, classData) => {
  try {
    const classRef = doc(db, CLASSES_COLLECTION, classId);
    const updateData = {
      ...classData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(classRef, updateData);
    return { id: classId, ...updateData };
  } catch (error) {
    console.error('Error updating class:', error);
    throw error;
  }
};

// Delete class
export const deleteClass = async (classId) => {
  try {
    const classRef = doc(db, CLASSES_COLLECTION, classId);
    await deleteDoc(classRef);
    return true;
  } catch (error) {
    console.error('Error deleting class:', error);
    throw error;
  }
};

// ============================================
// ATTENDANCE MANAGEMENT
// ============================================

// Get all attendance records
export const getAllAttendance = async () => {
  try {
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(attendanceRef, orderBy('classDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching attendance:', error);
    throw error;
  }
};

// Get attendance by enrollment ID
export const getAttendanceByEnrollmentId = async (enrollmentId) => {
  try {
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(
      attendanceRef,
      where('enrollmentId', '==', enrollmentId),
      orderBy('classDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching enrollment attendance:', error);
    throw error;
  }
};

// Get attendance by date
export const getAttendanceByDate = async (classDate) => {
  try {
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const q = query(
      attendanceRef,
      where('classDate', '==', classDate)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching attendance by date:', error);
    throw error;
  }
};

// Mark attendance
export const markAttendance = async (attendanceData) => {
  try {
    const attendanceRef = collection(db, ATTENDANCE_COLLECTION);
    const newAttendance = {
      ...attendanceData,
      markedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(attendanceRef, newAttendance);
    
    // Update enrollment attendance counts
    const enrollmentRef = doc(db, ONLINECLASS_COLLECTION, attendanceData.enrollmentId);
    const enrollmentDoc = await getDoc(enrollmentRef);
    
    if (enrollmentDoc.exists()) {
      const data = enrollmentDoc.data();
      let updateObj = Object.assign({}, { updatedAt: serverTimestamp() });
      
      let newAttended = data.attendedDays || 0;
      let newAbsent = data.absentDays || 0;

      if (attendanceData.status === 'present') {
        newAttended += 1;
        updateObj.completedClasses = (data.completedClasses || 0) + 1;
        updateObj.attendedDays = newAttended;
      } else if (attendanceData.status === 'absent') {
        newAbsent += 1;
        updateObj.absentDays = newAbsent;
      }

      const totalClasses = data.totalClasses || 0;
      if (newAttended + newAbsent >= totalClasses && totalClasses > 0) {
        updateObj.status = 'completed';
        updateObj.courseEndDate = serverTimestamp();
      }

      await updateDoc(enrollmentRef, updateObj);
    }
    
    return { id: docRef.id, ...newAttendance };
  } catch (error) {
    console.error('Error marking attendance:', error);
    throw error;
  }
};

// Update attendance
export const updateAttendance = async (attendanceId, attendanceData) => {
  try {
    const attendanceRef = doc(db, ATTENDANCE_COLLECTION, attendanceId);
    const updateData = {
      ...attendanceData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(attendanceRef, updateData);
    return { id: attendanceId, ...updateData };
  } catch (error) {
    console.error('Error updating attendance:', error);
    throw error;
  }
};

// ============================================
// ANALYTICS & REPORTS
// ============================================

// Get enrollment statistics
export const getEnrollmentStats = async () => {
  try {
    const enrollments = await getAllEnrollments();
    
    const stats = {
      total: enrollments.length,
      active: enrollments.filter(e => e.status === 'active').length,
      completed: enrollments.filter(e => e.status === 'completed').length,
      dropped: enrollments.filter(e => e.status === 'dropped').length,
    };
    
    return stats;
  } catch (error) {
    console.error('Error fetching enrollment stats:', error);
    throw error;
  }
};

// Get course completion rate
export const getCourseCompletionRate = async (courseId) => {
  try {
    const enrollments = await getEnrollmentsByCourseId(courseId);
    const completed = enrollments.filter(e => e.status === 'completed').length;
    const total = enrollments.length;
    
    return {
      total,
      completed,
      rate: total > 0 ? (completed / total) * 100 : 0,
    };
  } catch (error) {
    console.error('Error fetching course completion rate:', error);
    throw error;
  }
};

// Get students who completed their courses
export const getCompletedStudents = async () => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const q = query(
      enrollmentsRef,
      where('status', '==', 'completed'),
      orderBy('courseEndDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching completed students:', error);
    throw error;
  }
};

// ============================================
// PAYMENT MANAGEMENT
// ============================================

// Record a payment for an enrollment
export const recordPayment = async (enrollmentId, paymentData) => {
  try {
    const enrollmentRef = doc(db, ONLINECLASS_COLLECTION, enrollmentId);
    const enrollmentDoc = await getDoc(enrollmentRef);
    
    if (!enrollmentDoc.exists()) {
      throw new Error('Enrollment not found');
    }
    
    const enrollment = enrollmentDoc.data();
    const currentPaid = parseFloat(enrollment.amountPaid) || 0;
    const paymentAmount = parseFloat(paymentData.amount) || 0;
    const newAmountPaid = currentPaid + paymentAmount;
    const totalAmount = parseFloat(enrollment.totalAmount) || 0;
    const newBalance = totalAmount - newAmountPaid;
    
    // Determine payment status
    let paymentStatus = 'pending';
    if (newAmountPaid >= totalAmount) {
      paymentStatus = 'paid';
    } else if (newAmountPaid > 0) {
      paymentStatus = 'partial';
    }
    
    // Update enrollment with new payment info
    await updateDoc(enrollmentRef, {
      amountPaid: newAmountPaid,
      balance: newBalance,
      paymentStatus,
      lastPaymentDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    // Add payment to payment history
    const paymentHistory = enrollment.paymentHistory || [];
    paymentHistory.push({
      amount: paymentAmount,
      paymentMethod: paymentData.paymentMethod || 'cash',
      paymentDate: paymentData.paymentDate || new Date().toISOString(),
      notes: paymentData.notes || '',
      recordedAt: new Date().toISOString(),
    });
    
    await updateDoc(enrollmentRef, {
      paymentHistory,
    });
    
    return {
      success: true,
      newAmountPaid,
      newBalance,
      paymentStatus,
    };
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};

// Get all enrollments with pending or partial payments
export const getPendingPayments = async () => {
  try {
    const enrollmentsRef = collection(db, ONLINECLASS_COLLECTION);
    const q1 = query(
      enrollmentsRef,
      where('paymentStatus', '==', 'pending')
    );
    const q2 = query(
      enrollmentsRef,
      where('paymentStatus', '==', 'partial')
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2),
    ]);
    
    const pending = snapshot1.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    
    const partial = snapshot2.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    
    return [...pending, ...partial];
  } catch (error) {
    console.error('Error fetching pending payments:', error);
    throw error;
  }
};

// Get enrollments with overdue payments
export const getOverduePayments = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const allEnrollments = await getAllEnrollments();
    
    // Filter for overdue payments (payment due date has passed and not fully paid)
    const overdue = allEnrollments.filter(enrollment => {
      if (enrollment.paymentStatus === 'paid') return false;
      
      if (enrollment.paymentDueDate) {
        const dueDate = new Date(enrollment.paymentDueDate);
        return dueDate < today;
      }
      
      return false;
    });
    
    return overdue;
  } catch (error) {
    console.error('Error fetching overdue payments:', error);
    throw error;
  }
};

// Update payment status based on amounts (can be called manually to recalculate)
export const updatePaymentStatus = async (enrollmentId) => {
  try {
    const enrollmentRef = doc(db, ONLINECLASS_COLLECTION, enrollmentId);
    const enrollmentDoc = await getDoc(enrollmentRef);
    
    if (!enrollmentDoc.exists()) {
      throw new Error('Enrollment not found');
    }
    
    const enrollment = enrollmentDoc.data();
    const amountPaid = parseFloat(enrollment.amountPaid) || 0;
    const totalAmount = parseFloat(enrollment.totalAmount) || 0;
    
    let paymentStatus = 'pending';
    if (amountPaid >= totalAmount) {
      paymentStatus = 'paid';
    } else if (amountPaid > 0) {
      paymentStatus = 'partial';
    }
    
    const balance = totalAmount - amountPaid;
    
    await updateDoc(enrollmentRef, {
      paymentStatus,
      balance,
      updatedAt: serverTimestamp(),
    });
    
    return { paymentStatus, balance };
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw error;
  }
};

// Get payment statistics
export const getPaymentStats = async () => {
  try {
    const enrollments = await getAllEnrollments();
    
    const totalPending = enrollments
      .filter(e => e.paymentStatus === 'pending' || e.paymentStatus === 'partial')
      .reduce((sum, e) => sum + (parseFloat(e.balance) || 0), 0);
    
    const totalPaid = enrollments
      .reduce((sum, e) => sum + (parseFloat(e.amountPaid) || 0), 0);
    
    const overduePayments = await getOverduePayments();
    
    return {
      totalPending,
      totalPaid,
      overdueCount: overduePayments.length,
      paidCount: enrollments.filter(e => e.paymentStatus === 'paid').length,
      pendingCount: enrollments.filter(e => e.paymentStatus === 'pending').length,
      partialCount: enrollments.filter(e => e.paymentStatus === 'partial').length,
    };
  } catch (error) {
    console.error('Error fetching payment stats:', error);
    throw error;
  }
};
